import StatisticsPage, { drilldownPath } from '../../components/statistics/StatisticsPage';
import StatTile from '../../components/statistics/StatTile';
import DataTable, { Column } from '../../components/statistics/DataTable';
import { BarList } from '../../components/statistics/charts';
import { useCampuses } from '../../components/statistics/PeriodBar';
import { useStatisticsData, useStatisticsFilters } from '../../components/statistics/useStatistics';
import { getCaptures } from '../../services/statisticsService';
import { formatCount, formatDecimal, formatDays, formatHours, formatShare } from '../../utils/period';

interface Segment {
  key: string;
  created: number;
  alerted: number;
  seatConfirmed: number;
  alertedShare: number | null;
  seatShare: number | null;
}

const ENTRY_POINT_LABELS: Record<string, string> = {
  search_empty: 'After a search that found nothing',
  search_results: 'After a search that found rides',
  captures_screen: 'From the Ride Captures screen',
  unknown: 'Unknown',
};

const segmentColumns = (keyLabel: string, describe: (key: string) => string = (k) => k): Column<Segment>[] => [
  { key: 'key', label: keyLabel, value: (s) => describe(s.key) },
  { key: 'created', label: 'Created', numeric: true },
  { key: 'alertedShare', label: 'Alerted', numeric: true, render: (s) => formatShare(s.alertedShare) },
  { key: 'seatShare', label: 'Ended in a seat', numeric: true, render: (s) => formatShare(s.seatShare) },
];

/** PRD §5.3 — do Ride Captures turn a failed search into a seat? */
const RideCaptures = () => {
  const [filters, setFilters] = useStatisticsFilters();
  const { data, loading, error, reload } = useStatisticsData(getCaptures, filters);
  const campuses = useCampuses();
  const campusName = (id: string) => campuses.find((c) => c.id === id)?.name || (id === 'unknown' ? 'Unknown' : id);

  const f = data?.funnel || {};
  const pf = data?.previousFunnel || {};
  const share = (part?: number, whole?: number) => (whole ? (part || 0) / whole : null);
  const segments = data?.segments || {};

  const funnelSteps = [
    { label: 'Captures created', value: f.created },
    { label: 'Alerted at least once', sub: 'The success bar', value: f.alerted },
    { label: 'Match viewed in the app', sub: 'Not a push being opened', value: f.viewed },
    { label: 'Rider asked for the seat', value: f.actedOn },
    { label: 'Seat confirmed', value: f.seatConfirmed },
  ];

  return (
    <StatisticsPage
      title="Ride Captures"
      subtitle="Captures created in the period, followed from the first alert to a confirmed seat."
      filters={filters}
      onFiltersChange={setFilters}
      period={data?.period}
      loading={loading}
      error={error}
      onRetry={reload}
    >
      <div className="stats-callout">
        A Capture counts as <strong>successful if it alerted its owner at least once</strong>. Whether it then ended in a
        seat is reported separately: a wide gap between the two points at the rides being offered, not at the watching.
      </div>

      <div className="stats-tiles">
        <StatTile
          label="Captures created"
          current={f.created ?? null}
          previous={pf.created ?? null}
          format={formatCount}
          to={drilldownPath('captures', filters)}
        />
        <StatTile
          label="Success rate (alerted)"
          current={share(f.alerted, f.created)}
          previous={share(pf.alerted, pf.created)}
          format={formatShare}
          detail={`${formatCount(f.ridersNotified)} riders alerted`}
        />
        <StatTile
          label="Ended in a seat"
          current={share(f.seatConfirmed, f.created)}
          previous={share(pf.seatConfirmed, pf.created)}
          format={formatShare}
        />
        <StatTile
          label="Time to first alert"
          current={data?.timeToFirstMatch?.medianHours ?? null}
          format={formatHours}
          detail="Median"
        />
        <StatTile
          label="Never matched"
          current={data?.neverMatched?.count ?? null}
          format={formatCount}
          detail={`${formatShare(data?.neverMatched?.share)} of Captures`}
        />
        <StatTile
          label="Abandoned"
          current={data?.abandonment?.count ?? null}
          format={formatCount}
          detail={`${formatShare(data?.abandonment?.share)} · median ${formatDays(data?.abandonment?.medianDaysSurvived)} before`}
          note="Paused without ever giving a seat. Deleted Captures leave no record."
        />
        <StatTile
          label="Matches dismissed"
          current={data?.declinedMatches?.rate ?? null}
          format={formatShare}
          detail={`${formatCount(data?.declinedMatches?.declined)} of ${formatCount(data?.declinedMatches?.shown)} shown`}
          note="High means poor suggestions, not a failing feature."
        />
        <StatTile
          label="Riders at the Capture limit"
          current={data?.ridersAtLimit?.count ?? null}
          format={formatCount}
          detail={`${data?.ridersAtLimit?.limit ?? 3} active at once, right now`}
        />
      </div>

      <div className="stats-columns">
        <section className="stats-section">
          <h2>Funnel</h2>
          <BarList
            max={f.created || 1}
            items={funnelSteps.map((s) => ({
              label: s.label,
              sub: s.sub,
              value: s.value || 0,
              display: `${formatCount(s.value)} · ${formatShare(share(s.value, f.created))}`,
            }))}
          />
        </section>
        <section className="stats-section">
          <h2>Time to first alert</h2>
          <BarList
            items={(data?.timeToFirstMatch?.distribution || []).map((b: any) => ({ label: b.label, value: b.count }))}
          />
        </section>
      </div>

      <div className="stats-columns">
        <section className="stats-section">
          <h2>Never matched, by route</h2>
          <p className="stats-section-intro">Riders who said exactly what they wanted and never got it.</p>
          <DataTable
            columns={[
              { key: 'route', label: 'Route', value: (r: any) => `${r.origin} → ${r.destination}` },
              { key: 'count', label: 'Captures', numeric: true },
            ]}
            rows={data?.neverMatched?.byRoute || []}
            exportName="zippel-captures-never-matched"
          />
        </section>
        <section className="stats-section">
          <h2>Never matched, by time of day</h2>
          <BarList
            items={Object.entries(data?.neverMatched?.byWindow || {}).map(([window, count]) => ({
              label: window,
              value: count as number,
            }))}
          />
        </section>
      </div>

      <div className="stats-columns">
        <section className="stats-section">
          <h2>One-off or recurring</h2>
          <DataTable
            columns={segmentColumns('Kind')}
            rows={segments.kind || []}
            exportName="zippel-captures-by-kind"
            rowKey={(s) => s.key}
          />
        </section>
        <section className="stats-section">
          <h2>Where the Capture was created</h2>
          {data?.entryPointStatus === 'ok' ? (
            <>
              <DataTable
                columns={segmentColumns('Created from', (k) => ENTRY_POINT_LABELS[k] || k)}
                rows={segments.entryPoint || []}
                exportName="zippel-captures-by-entry-point"
                rowKey={(s) => s.key}
              />
              <p className="stats-note">
                Best-effort, from in-app analytics: the owner's last form-open in the hour before. Leaves out web-only
                members and anyone who declined analytics.
              </p>
            </>
          ) : (
            <p className="stats-empty">
              {data?.entryPointStatus === 'not_yet_sent'
                ? 'Not available yet. It needs in-app analytics: the app does not report where the Capture form was opened from.'
                : 'Not available: PostHog is not configured or could not be reached.'}
            </p>
          )}
        </section>
      </div>

      <div className="stats-columns">
        <section className="stats-section">
          <h2>By route</h2>
          <DataTable columns={segmentColumns('Route')} rows={segments.route || []} exportName="zippel-captures-by-route" rowKey={(s) => s.key} />
        </section>
        <section className="stats-section">
          <h2>By campus</h2>
          <DataTable
            columns={segmentColumns('Campus', campusName)}
            rows={segments.campus || []}
            exportName="zippel-captures-by-campus"
            rowKey={(s) => s.key}
          />
        </section>
      </div>

      <section className="stats-section">
        <h2>Does it change behaviour?</h2>
        <p className="stats-section-intro">
          Riders whose first Capture was in this period, against riders who asked for a seat in this period and never
          created one. Each followed for {data?.behaviour?.windowDays ?? 30} days.
        </p>
        <DataTable
          columns={[
            { key: 'group', label: 'Riders' },
            { key: 'riders', label: 'How many', numeric: true },
            { key: 'averageRidesTaken', label: 'Rides taken, average', numeric: true, render: (r: any) => formatDecimal(r.averageRidesTaken) },
            { key: 'shareWithARide', label: 'Took at least one', numeric: true, render: (r: any) => formatShare(r.shareWithARide) },
          ]}
          rows={
            data?.behaviour
              ? [
                  { group: 'Created a Capture', ...data.behaviour.withCapture },
                  { group: 'Never created one', ...data.behaviour.without },
                ]
              : []
          }
          rowKey={(r: any) => r.group}
        />
        <p className="stats-note">
          The second group starts from their first seat request, which already shows intent, so the comparison leans in
          its favour.
        </p>
      </section>
    </StatisticsPage>
  );
};

export default RideCaptures;
